<script setup lang="ts">
import FeatherMail from '@/components-svg/featherMail.vue'
import InfoModal from '@/components/InfoModal.vue'
import { useStore } from '@/composables/useStore'
import { IActivityInvite } from '@/services/activityInviteService'
import { IAfspraakInvite } from '@/services/afspraakInviteService'
import { emitter } from '@/services/emitter'
import { computed, ref } from 'vue'

const { activityInviteStore, afspraakInviteStore } = useStore()

const showModal = ref(false)

const unreadInvites = computed(() => {
    const activityInvites = activityInviteStore.getters.value.getReceivedInvites.filter((invite) => invite.status === 'Verstuurd')
    const afspraakInvites = afspraakInviteStore.getters.value.getReceivedAfspraakInvites.filter((invite) => invite.status === 'Verstuurd')
    return [...activityInvites, ...afspraakInvites]
})

const unreadInvitesCount = computed(() => {
    return unreadInvites.value.length
})

const getInviteText = () => {
    return `Je hebt ${unreadInvitesCount.value} onbeantwoorde uitnodiging${unreadInvitesCount.value > 1 ? 'en' : ''}`
}

const onOpenInvites = () => {
    showModal.value = true
}

const closeModal = () => {
    showModal.value = false
}

const acceptInvite = async (invite: IActivityInvite | IAfspraakInvite) => {
    try {
        if ('activiteitIngeplandId' in invite) {
            // This is an activity invite
            await activityInviteStore.actions.acceptActivityInvite(invite.id)
            await activityInviteStore.actions.getReceivedActivityInvites()
        } else {
            // This is an afspraak invite
            await afspraakInviteStore.actions.acceptAfspraakInvite(invite.id)
            await afspraakInviteStore.actions.getReceivedAfspraakInvites()
        }
        emitter.emit('invite-accepted', invite)
    } catch (error) {
        console.error('Error accepting invite:', error)
    }
}

const declineInvite = async (invite: IActivityInvite | IAfspraakInvite) => {
    try {
        if ('activiteitIngeplandId' in invite) {
            // This is an activity invite
            await activityInviteStore.actions.rejectActivityInvite(invite.id)
            await activityInviteStore.actions.getReceivedActivityInvites()
        } else {
            // This is an afspraak invite
            await afspraakInviteStore.actions.rejectAfspraakInvite(invite.id)
            await afspraakInviteStore.actions.getReceivedAfspraakInvites()
        }
    } catch (error) {
        console.error('Error declining invite:', error)
    }
}
</script>

<template>
    <div
        id="nav-notifications"
        class="navbar-custom__notifications mr-4"
    >
        <tippy
            placement="bottom"
            class="time-block-icon"
        >
            <div
                class="navbar-custom__notifications__icon"
                @click="onOpenInvites"
            >
                <div class="navbar-custom__notifications__icon__number">{{ unreadInvitesCount > 9 ? '9+' : unreadInvitesCount }}</div>
                <FeatherMail class="text-primary" />
            </div>
            <template #content>
                <div
                    v-if="unreadInvitesCount === 0"
                    class="p-2"
                >
                    {{ `Je hebt geen openstaande uitnodigingen` }}
                </div>
                <div
                    v-else
                    class="p-2"
                >
                    <span>{{ getInviteText() }}</span>
                    <div class="mt-2">
                        <small class="text-muted">Klik om alle uitnodigingen te bekijken</small>
                    </div>
                </div>
            </template>
        </tippy>
    </div>

    <!-- Pending Invites Modal -->
    <info-modal
        v-model="showModal"
        @close-modal="closeModal"
    >
        <template #title>
            <h4 class="mb-0">Openstaande Uitnodigingen</h4>
        </template>
        <template #content>
            <div
                v-if="unreadInvitesCount === 0"
                class="text-center py-4"
            >
                <p class="mb-0">Je hebt geen openstaande uitnodigingen.</p>
            </div>
            <div v-else>
                <div
                    v-for="invite in unreadInvites"
                    :key="invite.id"
                    class="invite-item p-3 mb-3 border rounded"
                >
                    <div class="d-flex justify-content-between align-items-start">
                        <div class="flex-grow-1">
                            <h6 class="mb-1">{{ 'activiteitNaam' in invite ? invite.activiteitNaam : invite.afspraakTitel }}</h6>
                            <p class="mb-1 small"><strong>Van:</strong> {{ invite.vanStamgroepNaam }}</p>
                            <p class="mb-1 small"><strong>Op:</strong> {{ new Date('activiteitDatumTijd' in invite ? invite.activiteitDatumTijd : invite.afspraakDatumStart).toLocaleString() }}</p>
                            <p
                                v-if="invite.bericht"
                                class="mb-2 small"
                            >
                                {{ invite.bericht }}
                            </p>
                        </div>
                    </div>
                    <div class="d-flex gap-2 mt-3">
                        <button
                            class="btn btn-sm btn-primary mr-3"
                            @click="acceptInvite(invite)"
                        >
                            Accepteren
                        </button>
                        <button
                            class="btn btn-sm btn-outline-primary"
                            @click="declineInvite(invite)"
                        >
                            Afwijzen
                        </button>
                    </div>
                </div>
            </div>
        </template>
        <template #button>
            <button
                class="btn btn-primary btn-sm"
                @click="closeModal"
            >
                Sluiten
            </button>
        </template>
    </info-modal>
</template>

<style scoped lang="scss">
ul {
    list-style-type: none;
    padding-left: 0;
    margin-bottom: 0;
}

.invite-item {
    background-color: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    transition: box-shadow 0.15s ease-in-out;

    h6 {
        color: #212529;
        font-weight: 600;
        font-size: 1rem;
    }
}
</style>
